/**
 * useProjectWorkspace — Generic sector workspace hook.
 * Fetches journey + evidence for any non-housing sector project.
 * Same pattern as useHousingJourney but sector-agnostic.
 */
import { useState, useEffect, useCallback } from "react";
import client from "@/api/client";

let _cache = {};     // keyed by assessment_id
let _cacheAt = {};
const TTL = 30_000;

export function invalidateProjectWorkspaceCache(assessmentId) {
  delete _cache[assessmentId];
  delete _cacheAt[assessmentId];
}

// Expose project name from cache for sidebar (no full hook overhead)
export function getProjectNameFromCache(assessmentId) {
  return _cache[assessmentId]?.journey?.name || null;
}

function deriveEvidenceCoverage(evidenceData) {
  const items   = evidenceData?.items || [];
  const cov     = evidenceData?.coverage || {};
  const summary = evidenceData?.summary  || {};
  const verified = new Set(items.filter(e => e.verification_status === "verified").map(e => e.linked_parameter));
  const uploaded = new Set(items.filter(e => ["uploaded","under_review","verified"].includes(e.verification_status)).map(e => e.linked_parameter));
  return {
    items, verified, uploaded,
    coverage_pct:    cov.coverage_pct   || 0,
    verified_pct:    cov.verified_pct   || 0,
    audit_ready_pct: cov.audit_ready_pct || 0,
    audit_ready:     cov.audit_ready    || false,
    covered_params:  cov.covered_params || [],
    missing_params:  cov.missing_params || [],
    critical_missing:cov.critical_missing || [],
    required_params: cov.required_params|| [],
    section_coverage:cov.section_coverage || {},
    total_verified:  summary.verified   || 0,
    total_items:     summary.total      || 0,
    has:       (p) => uploaded.has(p),
    isVerified:(p) => verified.has(p),
  };
}

export function useProjectWorkspace(assessmentId, { autoFetch = true } = {}) {
  const id = assessmentId ? String(assessmentId) : null;
  const [journey,   setJourney]   = useState(_cache[id]?.journey   || null);
  const [evidence,  setEvidence]  = useState(_cache[id]?.evidence  || null);
  const [sectorCfg, setSectorCfg] = useState(_cache[id]?.sectorCfg || null);
  const [loading,   setLoading]   = useState(!_cache[id]);
  const [error,     setError]     = useState(null);

  const fetchAll = useCallback(async (force = false) => {
    if (!id) return;
    const now = Date.now();
    if (!force && _cache[id] && (now - (_cacheAt[id] || 0)) < TTL) {
      setJourney(_cache[id].journey);
      setEvidence(_cache[id].evidence);
      setSectorCfg(_cache[id].sectorCfg);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [jRes, eRes] = await Promise.all([
        client.get(`/workspace/project/${id}/journey`),
        client.get(`/workspace/project/${id}/evidence`),
      ]);
      const j = jRes.data;
      const e = eRes.data;
      // Load sector config if sector is known
      let cfg = null;
      if (j.sector) {
        try {
          const cRes = await client.get(`/workspace/sector-config/${j.sector}`);
          cfg = cRes.data?.config || null;
        } catch { /* sector config optional */ }
      }
      _cache[id]   = { journey: j, evidence: e, sectorCfg: cfg };
      _cacheAt[id] = Date.now();
      setJourney(j);
      setEvidence(e);
      setSectorCfg(cfg);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const refresh = useCallback(() => {
    if (id) {
      delete _cache[id];
      delete _cacheAt[id];
    }
    fetchAll(true);
  }, [id, fetchAll]);

  useEffect(() => {
    if (autoFetch) fetchAll();
  }, [autoFetch, fetchAll]);

  const evidenceCoverage = deriveEvidenceCoverage(evidence);

  return {
    journey, evidence, sectorCfg, loading, error, refresh,
    assessmentId:   id,
    sector:         journey?.sector,
    sectorLabel:    journey?.sector_label,
    methodology:    journey?.methodology,
    // Semantic methodology architecture (3 layers)
    calculationBasis:  journey?.calculation_basis || "IPCC 2006 Guidelines",
    protocol:          journey?.protocol || journey?.methodology || "",
    creditingPathway:  journey?.crediting_pathway ?? null,   // null → "Not Assigned" in UI
    dataSource:     journey?.data_source,
    isDbBacked:     journey?.data_source === "db_backed",
    isEmpty:        journey?.data_source === "empty",
    workflowStatus: journey?.workflow_status || journey?.status || "draft",
    calcVersion:    journey?.version,
    // Normalized output slices
    baseline:       journey?.baseline,
    projectScenario:journey?.project_scenario,
    reduction:      journey?.reduction,
    coBenefits:     journey?.co_benefits,
    financial:      journey?.financial,
    sensitivity:    journey?.sensitivity_analysis,
    carbonPathway:  journey?.carbon_pathway,
    pathways:       journey?.carbon_pathway?.pathways || {},
    factors:        journey?.factors_used,
    kpis:           journey?.kpis,
    org:            journey?.organization || {},
    inputSnapshot:  journey?.input_snapshot || {},    // last saved inputs for form pre-population
    draftInputSnapshot: journey?.draft_input_snapshot || {},  // draft (unsaved) inputs
    projectState:   journey?.project_state  || null,  // Central Decision Engine — use this, not local logic
    // Global project stage — single canonical state for ALL pages
    projectStage:      journey?.project_state?.stage        || "MODEL_INITIALIZED",
    projectStageLabel: journey?.project_state?.stage_label  || "Model Initialized",
    // VRS — Verification Readiness Score — canonical backend value, no client recomputation
    vrsScore:          journey?.project_state?.vrs?.score        ?? null,
    vrsBand:           journey?.project_state?.vrs?.band         || "Early Stage",
    scope2BaselineSource: journey?.project_state?.vrs?.scope2_baseline_source || null,
    scope2Basis:       journey?.project_state?.vrs?.scope2_basis || null,
    scope2DisplacementType: journey?.project_state?.vrs?.scope2_displacement_type || null,
    // Verification Status — driven ONLY by explicit VVB workflow (never inferred from VRS or completeness)
    verificationStatus:      journey?.project_state?.verification_status?.code  || "NOT_INITIATED",
    verificationStatusLabel: journey?.project_state?.verification_status?.label || "Verification: Not Initiated",
    verificationVvbLabel:    journey?.project_state?.verification_status?.vvb_label || null,
    sdgContributions: journey?.sdg_contributions || [],
    pathwayOutput:    journey?.pathway_output    || {},
    dealReadiness:    journey?.deal_readiness    || null,
    validationStatus: journey?.validation_status || null,
    dataConfidence:   journey?.data_confidence   || null,
    assumptionFlags:  journey?.assumption_flags_rated || [],
    registryMapping:  journey?.registry_mapping  || null,
    // Biogas-specific structured sections (when available)
    commercialStructure:     journey?.commercial_structure,
    feedstockSupplySecurity: journey?.feedstock_supply_security,
    capexBreakdown:          journey?.capex_breakdown,
    // ── STRUCTURED OUTPUTS (single source of truth) ─────────────────────────
    // All modules must read from these — never recompute independently
    derivedVariables:    journey?.derived_variables    || null,  // key metrics assembled from engine outputs
    circularityOutputs:  journey?.circularity_outputs  || null,  // formal circularity score (weighted methodology)
    climateOutputs:      journey?.climate_outputs      || null,  // climate snapshot source: baseline, reduction, methodology
    scopeOutputs:        journey?.climate_outputs?.scope_outputs || null,  // canonical per-scope breakdown (S1/S2/S3)
    financialOutputs:    journey?.financial_outputs    || null,  // expanded: revenue streams, contracted%, carbon scenarios
    factorSnapshot:      journey?.factor_snapshot      || null,  // emission factors used in this run
    dataQualityOutputs:  journey?.data_quality_outputs || null,  // confidence, evidence, completeness, audit_ready
    // Evidence
    evidenceCoverage,
  };
}
